import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useAppStore } from '../context/AppStore';
import { useAuth } from '../context/AuthContext';
import { Card, StatCard, Badge, Button, NetworkIcon, ProgressBar } from '../components/ui';
import { NETWORK_CONFIG } from '../api/mockData';

const STATUS_COLOR = { published: '#10b981', partial: '#f59e0b', failed: '#ef4444', scheduled: '#3b82f6' };
const STATUS_LABEL = { published: 'Publicado', partial: 'Parcial', failed: 'Fallido', scheduled: 'Programado' };

/* Grid de 2 columnas que colapsa a 1 en móvil */
function TwoColGrid({ children, right = '320px' }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `1fr ${right}`,
      gap: '16px',
      alignItems: 'start',
    }}
      className="dashboard-grid"
    >
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const {
    analytics, analyticsLoading, fetchAnalytics,
    posts, postsLoading, fetchPosts,
    accounts, fetchAccounts,
  } = useAppStore();

  useEffect(() => {
    fetchAnalytics();
    fetchPosts();
    fetchAccounts();
  }, []);

  return (
    <>
      {/* Inyectar media query inline para el grid */}
      <style>{`
        @media (max-width: 767px) {
          .dashboard-grid {
            grid-template-columns: 1fr !important;
          }
          .stat-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .dashboard-grid {
            grid-template-columns: 1fr 260px !important;
          }
          .stat-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* ── Welcome ── */}
        <div className="animate-fadeIn" style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
        }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: '800' }}>
              Hola, {user?.name?.split(' ')[0]} 👋
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '2px' }}>
              {new Date().toLocaleDateString('es', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <Link to="/publish" style={{ textDecoration: 'none' }}>
            <Button size="lg" icon="⚡">Nueva publicación</Button>
          </Link>
        </div>

        {/* ── Stats Grid ── */}
        {analyticsLoading ? (
          <div className="stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} className="skeleton" style={{ height: '100px', borderRadius: 'var(--radius-lg)' }} />
            ))}
          </div>
        ) : analytics ? (
          <div className="stat-grid animate-fadeIn" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' }}>
            <StatCard icon="📤" label="Posts publicados"  value={analytics.totals.posts}       growth={analytics.growth.posts}    color="var(--accent-primary)" />
            <StatCard icon="👁"  label="Alcance total"    value={analytics.totals.reach}       growth={analytics.growth.reach}    color="#06b6d4" />
            <StatCard icon="❤️" label="Likes totales"     value={analytics.totals.likes}       growth={analytics.growth.likes}    color="#E1306C" />
            <StatCard icon="💬" label="Comentarios"       value={analytics.totals.comments}    growth={analytics.growth.comments} color="#f59e0b" />
            <StatCard icon="🔁" label="Compartidos"       value={analytics.totals.shares}      color="#10b981" />
            <StatCard icon="▶️" label="Reproducciones"    value={analytics.totals.video_views} color="#FF0000" />
          </div>
        ) : null}

        {/* ── Chart + Network breakdown ── */}
        <TwoColGrid right="300px">
          {/* Gráfico */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: '700' }}>Alcance últimos 30 días</h3>
              <Badge color="var(--accent-primary)">30 días</Badge>
            </div>
            {analyticsLoading ? (
              <div className="skeleton" style={{ height: '180px', borderRadius: 'var(--radius-md)' }} />
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={analytics?.chart || []}>
                  <defs>
                    <linearGradient id="reachGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#7c3aed" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fill: '#50506a', fontSize: 10 }} axisLine={false} tickLine={false} interval={6} />
                  <YAxis tick={{ fill: '#50506a', fontSize: 10 }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip
                    contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '12px' }}
                    labelStyle={{ color: 'var(--text-primary)' }}
                  />
                  <Area type="monotone" dataKey="reach" stroke="#7c3aed" strokeWidth={2} fill="url(#reachGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Por red social */}
          <Card>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: '700', marginBottom: '16px' }}>Por red social</h3>
            {analyticsLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: '40px', borderRadius: 'var(--radius-sm)' }} />)}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {(analytics?.by_network || []).map(net => {
                  const cfg = NETWORK_CONFIG[net.network];
                  const maxReach = Math.max(...(analytics?.by_network || []).map(n => n.reach));
                  return (
                    <div key={net.network}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <NetworkIcon network={net.network} size={22} />
                          <span style={{ fontSize: '13px', fontWeight: '600' }}>{cfg?.name}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{(net.reach / 1000).toFixed(1)}K</span>
                          <Badge color={cfg?.color} size="sm">{net.engagement}%</Badge>
                        </div>
                      </div>
                      <ProgressBar value={(net.reach / maxReach) * 100} color={cfg?.color} />
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </TwoColGrid>

        {/* ── Publicaciones recientes + Cuentas conectadas ── */}
        <TwoColGrid right="280px">
          {/* Publicaciones recientes */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: '700' }}>Publicaciones recientes</h3>
              <Link to="/posts" style={{ fontSize: '13px', color: 'var(--accent-primary)', textDecoration: 'none', fontWeight: '600' }}>Ver todas →</Link>
            </div>
            {postsLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: '60px', borderRadius: 'var(--radius-md)' }} />)}
              </div>
            ) : posts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)', fontSize: '14px' }}>
                📭 Aún no hay publicaciones. <Link to="/publish" style={{ color: 'var(--accent-primary)' }}>¡Crea una!</Link>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {posts.slice(0, 5).map((post, i) => (
                  <div key={post.id} className={`animate-fadeIn delay-${i + 1}`} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px', borderRadius: 'var(--radius-md)',
                    background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                  }}>
                    <div style={{
                      width: '42px', height: '42px', borderRadius: 'var(--radius-sm)',
                      background: 'var(--bg-hover)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: '18px', flexShrink: 0,
                    }}>
                      {post.media_type === 'video' ? '🎬' : '🖼'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', flexWrap: 'wrap' }}>
                        {post.networks.map(n => <NetworkIcon key={n.network} network={n.network} size={16} />)}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        {new Date(post.created_at + 'Z').toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                      <Badge color={STATUS_COLOR[post.status]}>{STATUS_LABEL[post.status]}</Badge>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        ❤️ {post.networks.reduce((a, n) => a + (n.likes || 0), 0).toLocaleString('es')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Cuentas conectadas */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: '700' }}>Cuentas conectadas</h3>
              <Link to="/accounts" style={{ fontSize: '13px', color: 'var(--accent-primary)', textDecoration: 'none', fontWeight: '600' }}>Gestionar →</Link>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {accounts.map((acc, i) => {
                const cfg = NETWORK_CONFIG[acc.network];
                return (
                  <div key={acc.id} className={`animate-fadeIn delay-${i + 1}`} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 12px', borderRadius: 'var(--radius-md)',
                    background: 'var(--bg-elevated)',
                    border: `1px solid ${acc.connected ? 'var(--border)' : '#ef444430'}`,
                  }}>
                    <NetworkIcon network={acc.network} size={26} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acc.name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        {acc.connected ? `${acc.followers?.toLocaleString('es') || 0} seguidores` : 'Desconectado'}
                      </div>
                    </div>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: acc.connected ? '#10b981' : '#ef4444', flexShrink: 0 }} />
                  </div>
                );
              })}
            </div>
          </Card>
        </TwoColGrid>

      </div>
    </>
  );
}
